/**
 * The seventh live route (#30 / R7.2) — the parent home screen, on real data.
 *
 * **What the server actually has.** `GET /api/dashboard-data` returns exactly this, and nothing
 * else (`dashboard.service.js:176-183`):
 *
 *     { users: [{ id, username, firstName, avatarUrl, weeklyReward, chores: [...] }],
 *       unassignedChores: [...],
 *       meta: { timestamp, timezone, isAdmin } }
 *
 * A member list, each member's quests, and the unassigned ones. No approvals, no week, no
 * payday, no pending-money ledger, and no currency.
 *
 * **The quiet trap this whole container is shaped around.** `dashboard.service.js:109` selects
 * completions with `tracked_time >= NOW() - INTERVAL '90 seconds'`, into a variable named
 * `allCompletedToday`. The name says today. The query says ninety seconds. So an empty
 * completion list means "nothing in the last minute or two", and the activity tile's own answer
 * to an empty list is **"No activity yet — completed quests will appear here."** A family that
 * finished twelve quests this morning would read that sentence about themselves.
 *
 * **What the SCREEN would say if you just handed it that payload.** Four tiles carry defaults
 * that read as facts about a real household and are not:
 *
 * | tile | its default | what the server has |
 * | --- | --- | --- |
 * | `ApprovalInboxTile` | "Unactioned approvals sweep into Friday's payday batch" | no approvals at all, and the payday period is per family (D-23) |
 * | `ApprovalInboxTile` | "All caught up" / "No approvals waiting" | a count nobody read |
 * | `WeekChapterTile` | "This week's chapter opens on Monday", "lanterns lit", "payday in" | no week, no payday |
 * | `PendingMoneyTile` | "Settles at Friday payday · approval on · dollars" | no pending ledger; `weeklyReward` is money EARNED |
 * | `ActivityFeedTile` | "No activity yet" | ninety seconds, not all history |
 * | the band subtitle | "6 of 8 quests lit today" | no "today" count is derivable |
 * | `Dashboard.jsx:526` | " — switch profile or preview kid mode" | nobody's name is known here |
 *
 * This is the same defect family `/approvals` shipped as **"3 of 0"** and `/village` shipped as
 * **"a new one begins on Monday"**.
 *
 * **THOSE THREE TILES NOW HAVE A CONTRACT, and this paragraph used to say they did not** (AD-06,
 * #579). The claim above about `GET /api/dashboard-data` is still exactly true — it answers a
 * member list and nothing else. What changed is that it is no longer the only read here.
 * `GET /api/dashboard-summary` (`dashboard.routes.js:45`) answers
 * `{kids, approvalCount, weekChapter, feedHead, meta}` **over the family's whole local day**, and
 * `GET /api/approvals` and `GET /api/payday/preview` fill the inbox and the pending ledger.
 *
 * **Those three are `requireAdmin`; the fourth, `/api/payday/window`, is `requireAuth` only.**
 * All four are secondary reads. A child is refused the three parent-only reads, while
 * `/api/dashboard-data` still renders the board. A failed window omits the payday caption.
 *
 * **The trap the server built on purpose, and the reason the approvals tile is not simply wired
 * to a number.** `dashboard.service.js:363`'s `pendingApprovalCount` catches its own failure and
 * **returns 0**. So `approvalCount: 0` means either "nobody is waiting" or "we could not find
 * out", while `ApprovalInboxTile`'s empty copy is "All caught up". The QUEUE is therefore the
 * authority when it answers, and an unconfirmed count says so in the page note. See
 * `lib/dashboardSummaryMapping.js`.
 *
 * **The current user's own name is NOT known.** No endpoint in this app returns the signed-in
 * member yet, so `parentName` is left unset and the page falls through to its nameless answer
 * rather than greeting an empty string.
 *
 * **Unassigned quests are counted, not listed.** The page has no slot for a quest with nobody
 * attached, and adding one is new UI rather than wiring. The count is real and is stated.
 *
 * **The vanishing-warning trap**, the same one `OnboardingRoute`, `ApprovalsRoute` and
 * `VillageRoute` document: going live leaves fixture mode, which silently REMOVES
 * `SnapshotNotice`. This route carries its own note instead.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOptionalActor } from '../state/ActorContext.jsx';
import LanternLoader from '../components/LanternLoader.jsx';
import { ApiError, api } from '../api/client.js';
import { toDashboard } from '../lib/dashboardMapping.js';
import { toSummaryTiles } from '../lib/dashboardSummaryMapping.js';
import { LIVE_LABELS, freshnessLead, useLiveRefresh } from '../state/HouseholdRealtime.jsx';

const Dashboard = lazy(() => import('../pages/Dashboard.jsx'));

/** Where the family story continues (#583 N1 S8, design D8). */
export const STORY_LINKS = [{ label: 'Open the village story', href: '#/village' }];

/**
 * Carried on the page, because leaving fixture mode removes the fixture banner. It opens with
 * `freshnessLead(status)`: "Live family data." only while the screen is live (#580).
 */
export const NOT_BUILT_NOTICE =
  '“Just now” only covers the last minute or two, not the whole day.';

/**
 * Said INSTEAD of naming the three tiles as unbuilt, when the viewer cannot read them.
 *
 * They are built now (AD-06). `GET /api/dashboard-summary`, `GET /api/approvals` and
 * `GET /api/payday/preview` are all `requireAdmin`, so for a child the tiles are hidden for a
 * REASON — not because nobody wrote them. Saying "not built yet" to a parent who can see them
 * would be false, and saying nothing to a child who cannot would be worse.
 */
export const PARENT_ONLY_NOTICE =
  'Approvals waiting, this week’s chapter and the pending-money ledger need a parent account, so '
  + 'those parts are not shown here.';

/**
 * Said when the parent reads FAILED rather than being refused.
 *
 * A 500 and a 403 both produced `null` tiles, and the note then told an authenticated PARENT that
 * the tiles "need a parent account" — a claim about their account made from a server fault. The
 * status is known, so the sentence can be right.
 */
export const PARENT_READ_FAILED =
  'We could not load approvals, this week’s chapter or the pending-money ledger just now, so those '
  + 'parts are not shown.';

/** Said when the two counts of one review queue do not match. Reported, never resolved. */
export function disagreementLine(summaryCount, queueCount) {
  return `The summary counts ${summaryCount} approvals waiting and the queue itself lists `
    + `${queueCount}. The queue is shown, and the two disagree.`;
}

/**
 * Said while the read is still in flight or has failed.
 *
 * The ready note opens with "Live family data." while live, which is a claim, and it is false at
 * the exact moment no data has arrived. Two sentences, each true in its own state.
 */
export const NOT_LIVE_YET =
  '“Just now” only covers the last minute or two, not the whole day. Approvals waiting, this '
  + 'week’s chapter and the pending-money ledger are not built into this screen yet, so those '
  + 'parts are not shown.';

/** `users: []` is an answer: nobody active in this family. It is not a failed read. */
export const NOBODY_YET =
  'No active family members yet. Add someone, and their quests appear here.';

export const EMPTY_TITLE = 'Nobody to show yet ✦';
export const EMPTY_BODY =
  'This screen lists each active family member and the quests they have coming up. Add a member '
  + 'and a quest, and they appear here.';

/**
 * The activity list's own words.
 *
 * The server bounds these completions to ninety seconds, so the tile is a "just now" feed. Its
 * default title ("Activity") and empty label ("No activity yet") both describe all of history,
 * which this list is not.
 */
export const ACTIVITY_TITLE = 'Just now';
export const ACTIVITY_EMPTY =
  'Nothing completed in the last minute or two. This list only covers that window.';

/**
 * The tile must NOT advertise itself as live unless it is.
 *
 * `ActivityFeedTile` defaults `live` to `true` and `kicker` to `'live'`. That renders the word
 * twice with a pulsing dot, and wires the list `aria-live="polite" aria-relevant="additions"` so
 * a screen reader announces additions. Found by the qwen3.8-max review as the thirteenth
 * un-chosen default. So `false` is the answer whenever the screen has no household connection.
 *
 * #580 made it an answer the screen EARNS: "● live" only while the socket is joined AND a read made
 * after that join has landed with nothing newer pending (`useLiveRefresh`). Otherwise the kicker
 * says what is true instead — `LIVE_LABELS` below.
 */
export const ACTIVITY_LIVE = false;

/** What the activity tile says when it is not live, by `useLiveRefresh` status (one home: the provider). */
export { LIVE_LABELS };

export const LOAD_FAILED =
  "We couldn't load your family's overview just now — give it another moment.";

/**
 * A 200 whose body is not the documented shape.
 *
 * `LOAD_FAILED` says "give it another moment", which promises a recovery a retry cannot produce:
 * the read SUCCEEDED and the answer was wrong. The 401 branch already has its own sentence, so
 * state-specific copy is the pattern here.
 */
export const LOAD_UNREADABLE =
  "Your family's overview came back in a shape we did not expect. Trying again may not help — "
  + 'please tell us if it keeps happening.';

/**
 * One sentence for a failed read.
 *
 * `dashboard.routes.js:27` applies `requireAuth` alone — not a parent-only guard — so every
 * signed-in member may read this, and a retry is a door that can actually open.
 */
export function loadMessage(error) {
  if (error instanceof ApiError && error.status === 401) {
    return 'Your sign-in has expired — please sign in again.';
  }
  return LOAD_FAILED;
}

/**
 * The unassigned line, stated only when there is something to state.
 *
 * Zero unassigned quests is a fact the server sent, but "0 unassigned quests" on a calm band is
 * noise rather than news, so the line is omitted at zero instead of reading as a false absence.
 */
/**
 * Stamp a "Just now" row in the FAMILY's timezone, not the browser's.
 *
 * `ActivityFeedTile`'s own formatter calls `toLocaleTimeString([], …)`, and an empty locale list
 * means the reader's zone. `meta.timezone` exists precisely to prevent that — the service looks
 * it up and uses it for its own week boundary — so throwing it away puts a travelling parent's
 * wall clock under a heading that claims to be the family's.
 */
export function makeTimeFormatter(timeZone) {
  return (value) => {
    if (value == null || value === '') return '';
    const when = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(when.getTime())) return '';
    try {
      return when.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}),
      });
    } catch {
      // An unknown zone from the server must not blank the row.
      return when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
  };
}

/**
 * Each parent-only read holds its OWN slot, so none of them can gate the board.
 *
 * Module scope, frozen, and built once — a fresh object per render would be a new `useEffect`
 * dependency every time, which is both a lint error and a re-read loop.
 */
const EMPTY_READ = Object.freeze({ done: false, value: null, error: null });
const FRESH_READS = Object.freeze({ summary: EMPTY_READ, queue: EMPTY_READ, preview: EMPTY_READ, window: EMPTY_READ });

export function unassignedLine(count) {
  if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) return '';
  return `${count} quest${count === 1 ? '' : 's'} with nobody assigned`;
}

export default function DashboardRoute() {
  const actorContext = useOptionalActor();
  // Isolated legacy hosts have no context. The running shell always supplies verified authority.
  const canReadParent = !actorContext || (actorContext.status === 'authenticated' && actorContext.actor?.kind === 'parent');
  const parentSeq = useRef({ summary: 0, queue: 0, preview: 0, window: 0 });
  const readParent = useCallback((key, path, signal) => {
    const seq = (parentSeq.current[key] += 1);
    api.get(path, { signal }).then((value) => {
      if (!signal.aborted && seq === parentSeq.current[key]) {
        setParent((prev) => ({ ...prev, [key]: { done: true, value, error: null } }));
      }
    }, (error) => {
      if (!signal.aborted && seq === parentSeq.current[key]) {
        const refused = error instanceof ApiError && [401, 403].includes(error.status);
        setParent((prev) => ({ ...prev, [key]: { done: true, value: refused ? null : prev[key].value, error } }));
      }
    });
  }, []);
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [parent, setParent] = useState(FRESH_READS);
  const [reloadKey, setReloadKey] = useState(0);
  const [recoveryKey, setRecoveryKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    // `/api/dashboard-data` is the PRIMARY read and the ONLY one this screen waits for.
    //
    // The first version put all four in one `Promise.all` behind a `settled()` wrapper. That stops
    // a REJECTION from failing the set and does nothing about a request that never settles, so a
    // board whose primary read had already arrived sat on the loading screen indefinitely. The
    // same reviewer found the identical defect in the Wallet's saving-goal read an hour earlier,
    // and this file repeated it. Each parent-only read now lands on its own.
    api.get('/api/dashboard-data', { signal: controller.signal })
      .then((data) => { if (live) setState({ status: 'ready', data, error: null }); })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState({ status: 'error', data: null, error });
      });
    return () => { live = false; controller.abort(); };
  }, [reloadKey, recoveryKey]);

  // Its own effect (d-20261001-04): the actor arriving after the board loaded adds these reads and
  // must not send the board back to `loading`.
  useEffect(() => {
    const controller = new AbortController();
    setParent(FRESH_READS);
    if (canReadParent) {
      readParent('summary', '/api/dashboard-summary', controller.signal);
      readParent('queue', '/api/approvals', controller.signal);
      readParent('preview', '/api/payday/preview', controller.signal);
      // #580 D2: the family's own payday, for the pending caption. Same read `/approvals` makes.
      readParent('window', '/api/payday/window', controller.signal);
    }
    return () => controller.abort();
  }, [reloadKey, canReadParent, readParent]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  // The first actor arrival can recover a failed cold read. Consume the arrival even if the
  // board is already ready or still loading, so it never resets a ready board or retries in a loop.
  const authenticated = actorContext?.status === 'authenticated';
  const firstActorSeen = useRef(authenticated);
  useEffect(() => {
    if (!authenticated || firstActorSeen.current) return;
    firstActorSeen.current = true;
    // Only retry the primary read; parent reads already start on this actor arrival.
    if (state.status === 'error') setRecoveryKey((n) => n + 1);
  }, [authenticated, state.status]);

  /**
   * #580: re-read when the household changes, in the background. The board is the one read the
   * refresh waits for. Each parent-only read lands on its own and only on SUCCESS, so a failure
   * keeps the tile it had; and one the server REFUSED (403) is not asked again, because a
   * child's 403 will not change on the next event.
   */
  const parentNow = useRef(parent);
  parentNow.current = parent;
  // Per read, the newest request wins: an older answer that lands late is dropped.
  const live = useLiveRefresh('dashboard', {
    read: (signal) => {
      if (canReadParent) {
        for (const [key, path] of [['summary', '/api/dashboard-summary'], ['queue', '/api/approvals'], ['preview', '/api/payday/preview'], ['window', '/api/payday/window']]) {
          const refused = parentNow.current[key].error instanceof ApiError && parentNow.current[key].error.status === 403;
          if (!refused) readParent(key, path, signal);
        }
      }
      return api.get('/api/dashboard-data', { signal });
    },
    apply: (data) => {
      if (!toDashboard(data)) return false;
      setState({ status: 'ready', data, error: null });
      return true;
    },
    enabled: state.status === 'ready',
  });

  const model = useMemo(() => toDashboard(state.data), [state.data]);

  /**
   * AD-06's three tiles. `null` throughout when the summary was refused, which `Dashboard.jsx`
   * reads as "not wired — hide the tile" rather than as an empty household.
   */
  const tiles = useMemo(
    () => (parent.summary.value
      ? toSummaryTiles(parent.summary.value, {
        queue: parent.queue.value,
        queueFailed: !!parent.queue.error,
        preview: parent.preview.value,
        previewFailed: !!parent.preview.error && !parent.preview.value,
        window: parent.window.value && parent.window.value.window ? parent.window.value.window : null,
      })
      : null),
    [parent],
  );
  /**
   * A payload this container does not recognise is a FAILED READ, not an empty family.
   *
   * `toDashboard` returns `null` for anything that is not the documented shape. Letting that
   * share a branch with `users: []` would put "No active family members yet" on the screen —
   * a confident statement about a household the read never described. That is the same defect
   * family this whole route exists to close, so it must not be reintroduced by the container.
   */
  const unreadable = state.status === 'ready' && model === null;
  const loadError = state.status === 'error'
    ? loadMessage(state.error)
    : unreadable ? LOAD_UNREADABLE : null;
  const ready = state.status === 'ready' && !unreadable;
  const hasMembers = !!(model && model.members.length > 0);
  const timeFormatter = useMemo(
    () => makeTimeFormatter(model ? model.timezone : null),
    [model],
  );

  // Both sentences share one home and one render site, so there is no second copy to drift.
  //
  // The unconfirmed-count warning rides HERE rather than on the tile, and that is a choice.
  // `Dashboard.jsx` does forward `approvals.note` to `ApprovalInboxTile` (#580 D2 made it, so this
  // route can blank the tile's "Friday's payday batch" default with `note: ''`). The warning stays
  // on the page note anyway: when the count is unconfirmed the tile is hidden, so a sentence on it
  // would never be seen, and this is the slot the screen renders for "what this cannot tell you".
  const notice = [freshnessLead(live.status), NOT_BUILT_NOTICE].filter(Boolean).join(' ');
  const base = !ready
    ? NOT_LIVE_YET
    : hasMembers ? notice : `${NOBODY_YET} ${notice}`;
  const parts = [base];
  if (ready && Object.values(parent).some((read) => read.error)) parts.push('Parent updates paused; retained details may be out of date.');
  if (ready && !tiles && parent.summary.done) {
    // A refusal and a fault are different sentences. `ApiError` carries the status, so this does
    // not have to guess.
    const status = parent.summary.error instanceof ApiError ? parent.summary.error.status : null;
    parts.push(status === 401 || status === 403 ? PARENT_ONLY_NOTICE : PARENT_READ_FAILED);
  } else if (ready && !tiles && !canReadParent && actorContext.status !== 'loading') {
    // Review M1: no parent read is sent here (a child, or a check that failed after the board loaded),
    // so say why the parts are missing. A failed check is not "you are not a parent".
    parts.push(actorContext.status === 'authenticated' ? PARENT_ONLY_NOTICE : PARENT_READ_FAILED);
  }
  if (ready && tiles && tiles.approvals.note) parts.push(tiles.approvals.note);
  if (ready && tiles && tiles.agreement.approvals.agree === false) {
    // Computed by the mapping and, in the first version, read by nobody. A disagreement that is
    // only recorded is a disagreement that was silently resolved.
    parts.push(disagreementLine(tiles.agreement.approvals.summary, tiles.agreement.approvals.queue));
  }
  const note = parts.join(' ');
  // Only a ready, readable board can settle; a failed one may still retry on the first actor
  // arrival. Parent-only reads do not count: a slow one must never hold the board (AD-06).
  const settled = ready && (!actorContext || actorContext.status !== 'loading');

  const kids = useMemo(() => (model ? model.members.map((m) => ({
    id: m.id,
    name: m.name,
    avatar: m.avatar,
    recap: m.recap,
    // NOT litCount / pendingCount / totalCount. Those render "X of Y quests lit today", and a
    // ninety-second completion window cannot answer that. The card hides them when absent.
  })) : []), [model]);

  return (
    <Suspense fallback={<LanternLoader label="Loading your family’s overview…" />}>
      <Dashboard
        showSnapshotNotice={false}
        kids={kids}
        // `null`, NOT `{}`. An empty object says "I read it, and there was nothing", and each of
        // these tiles then answers with its own warm copy: "All caught up", "This week's chapter
        // opens on Monday", "Nothing's pending this week". Every one is a claim about a real
        // household that nobody read. `null` says "I cannot fill this", and the tile is not
        // rendered at all.
        // AD-06. `null` still means "hide the tile", and that is the honest answer for a viewer
        // whose parent-only reads were refused.
        // `null` HIDES the tile. An unconfirmed count must hide it rather than render count 0 with
        // an empty list, which is `ApprovalInboxTile`'s "All caught up" state — a second sentence
        // contradicting the page note in the same view.
        approvals={ready && tiles && tiles.approvals.countKnown
          // `note: ''`: the tile's default footnote promises "Friday's payday batch", and the
          // payday is per family (#580 D2). The unconfirmed-count warning rides the page note.
          ? { count: tiles.approvals.count, items: tiles.approvals.items, note: '' }
          : null}
        week={ready && tiles ? tiles.week : null}
        pendingMoney={ready && tiles ? tiles.pendingMoney : null}
        currency={ready && tiles && tiles.pendingMoney ? tiles.pendingMoney.currency : undefined}
        activity={model ? model.justNow : []}
        activityTitle={ACTIVITY_TITLE}
        activityEmptyLabel={ACTIVITY_EMPTY}
        activityLive={live.status === 'live' ? true : ACTIVITY_LIVE}
        activityKicker={LIVE_LABELS[live.status] ?? null}
        activityFormatTime={timeFormatter}
        note={note}
        // #583 N1 S8 (D8): the village story is one link away. It reads nothing here and moves no money.
        storyLinks={STORY_LINKS}
        // No endpoint returns the signed-in member, so the page must stay nameless rather than
        // greet an empty string.
        parentName=""
        // The unassigned count is real. Every other part of the default subtitle counts "quests
        // lit today", which this payload cannot support.
        // `''` means NO subtitle. Until this change the page re-selected its computed default
        // for `''`, so the band's silence depended on three unrelated things happening to hold.
        bandSubtitle={model ? unassignedLine(model.unassignedCount) : ''}
        paydaySubtitle=""
        emptyTitle={EMPTY_TITLE}
        emptyBody={EMPTY_BODY}
        loading={state.status === 'loading'}
        settled={settled}
        error={loadError}
        onRetry={reload}
      />
    </Suspense>
  );
}
